import React, { useMemo } from "react";
import type { Appointment } from "../../../types";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Calendar, Clock, ChevronRight, RefreshCw, Sparkles } from "lucide-react";
import { navigate } from "../../../lib/router";
import { clientDetailsLabels } from "../labels";

interface Props {
  appointments: Appointment[];
}

export function UpcomingAppointmentsSection({ appointments }: Props) {
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].upcomingSection;

  const upcoming = useMemo(() => {
    const now = new Date();
    return appointments
      .filter(a => new Date(a.start_datetime) >= now && !a.done)
      .sort((a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime());
  }, [appointments]);

  if (upcoming.length === 0) return null;

  return (
    <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-neutral-50 flex items-center justify-between bg-gradient-to-r from-white to-neutral-50/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <Sparkles className="w-4 h-4" />
          </div>
          <span className="font-bold text-neutral-800 tracking-tight">{t.title}</span>
        </div>
        <span className="bg-primary/10 text-primary text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
          {upcoming.length}
        </span>
      </div>
      
      <div className="p-4 space-y-3">
        {upcoming.map(appt => {
          const date = new Date(appt.start_datetime);
          return (
            <button
              key={appt.id}
              onClick={() => navigate(`/app/appointments`)}
              className="w-full flex items-center justify-between p-4 rounded-2xl border border-neutral-50 hover:border-primary/20 hover:bg-primary/[0.02] transition-all group text-left relative overflow-hidden"
            >
              <div className="flex items-center gap-4 relative z-10">
                <div className="flex flex-col items-center justify-center h-14 w-14 rounded-2xl bg-neutral-50 group-hover:bg-primary/10 transition-colors text-neutral-600 group-hover:text-primary border border-neutral-100">
                  <span className="text-[9px] uppercase font-bold tracking-widest opacity-60">
                    {date.toLocaleDateString(lang === 'pt' ? 'pt-BR' : 'en-US', { month: 'short' })}
                  </span>
                  <span className="text-xl font-bold leading-none">
                    {date.getDate()}
                  </span>
                </div>
                <div>
                  <div className="font-bold text-neutral-900 mb-1 flex items-center gap-2">
                    {appt.specialty_name || t.defaultTitle}
                    {(appt.recurrence_group_id || appt.recurring_plan_id) && (
                      <RefreshCw className="h-3 w-3 text-primary animate-spin-slow" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-neutral-500 font-medium">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 opacity-40" />
                      {date.toLocaleTimeString(lang === 'pt' ? 'pt-BR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <span className="w-1 h-1 rounded-full bg-neutral-200" />
                    <span>{appt.duration_minutes} {t.minutes}</span>
                  </div>
                </div>
              </div>
              <div className="relative z-10 w-8 h-8 rounded-full bg-neutral-50 group-hover:bg-primary group-hover:text-white flex items-center justify-center transition-all">
                <ChevronRight className="h-4 w-4" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
